const MAX_EDGE = 2000;
const JPEG_QUALITY = 0.85;

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

function jpegName(name: string): string {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name) + ".jpg";
}

async function decode(file: File): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== "function") return null;
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    try {
      return await createImageBitmap(file);
    } catch {
      return null;
    }
  }
}

function toJpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
}

export async function downscaleImage(file: File): Promise<File> {
  if (!isImageFile(file)) return file;

  const bitmap = await decode(file);
  if (!bitmap) return file;

  const longest = Math.max(bitmap.width, bitmap.height);
  if (longest <= MAX_EDGE) {
    bitmap.close();
    return file;
  }

  const scale = MAX_EDGE / longest;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await toJpegBlob(canvas);
  if (!blob || blob.size >= file.size) return file;

  return new File([blob], jpegName(file.name), {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
}

const THUMB_EDGE = 320;

export async function makeThumbnail(file: File): Promise<string | null> {
  if (!isImageFile(file)) return null;

  const bitmap = await decode(file);
  if (!bitmap) return null;

  const scale = Math.min(1, THUMB_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return null;
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  try {
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch {
    return null;
  }
}
